/**
 * Cleaner Before photos / After photos — small shared pieces (progress segments, swipeable reference photos,
 * item chips, full-screen photo view). Per the Cleaner Before After Photos design.
 */
import { useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";

/** One segment per step: done teal, current teal outline, upcoming grey. */
export function Segments({ total, done, current }: { total: number; done: number; current?: number | null }) {
  return (
    <div className="flex items-center gap-1.5" aria-hidden>
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={cn(
            "h-1.5 flex-1 rounded-full",
            i < done ? "bg-[#24a89a]" : i === current ? "bg-[#9fd9d2]" : "bg-[#dde5ea]",
          )}
        />
      ))}
    </div>
  );
}

/** Item names as chips; the first few, then "+N". */
export function ItemChips({ names, max = 5 }: { names: string[]; max?: number }) {
  if (names.length === 0) return null;
  const shown = names.slice(0, max);
  const more = names.length - shown.length;
  return (
    <div className="flex flex-wrap gap-2">
      {shown.map((n, i) => (
        <span key={`${n}-${i}`} className="rounded-full bg-[#f1f5f7] px-3.5 py-1.5 text-[14px] font-bold text-[#0f1720]">
          {n}
        </span>
      ))}
      {more > 0 ? (
        <span className="rounded-full bg-[#e2eeed] px-3.5 py-1.5 text-[14px] font-bold text-[#15776d]">+{more}</span>
      ) : null}
    </div>
  );
}

/** Photo layouts by count: 1 full, 2 side by side, 3 = one tall + two stacked, 4+ = 2x2. */
function CollageGrid({ photos, gap = "gap-0.5" }: { photos: { url: string; name: string }[]; gap?: string }) {
  const shown = photos.slice(0, 4);
  const n = shown.length;
  return (
    <div className={cn("grid h-full w-full", gap, n === 1 ? "grid-cols-1" : "grid-cols-2", n >= 3 && "grid-rows-2")}>
      {shown.map((p, i) => (
        <img
          key={p.url}
          src={p.url}
          alt={p.name}
          className={cn("h-full w-full object-cover", n === 3 && i === 0 && "row-span-2")}
        />
      ))}
    </div>
  );
}

/** A spot with no reference photo yet: show the photos of what lives in it (taken when the unit was set up). */
export function ItemCollage({
  photos,
  label,
  corner,
}: {
  photos: { url: string; name: string }[];
  label: string;
  corner?: React.ReactNode;
}) {
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[20px] bg-[#e6edf1]">
      <CollageGrid photos={photos} />
      <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1.5 text-[12.5px] font-bold text-[#0f1720]">
        {label}
      </div>
      {corner ? <div className="absolute bottom-3 right-3">{corner}</div> : null}
    </div>
  );
}

/** The same collage as a small corner thumbnail on the camera. */
export function CollageThumb({
  photos,
  label,
  onClick,
}: {
  photos: { url: string; name: string }[];
  label: string;
  onClick?: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="flex flex-col items-end gap-1">
      <div className="h-[78px] w-[104px] overflow-hidden rounded-[12px] border-2 border-white shadow-[0_4px_14px_rgba(15,23,32,0.25)]">
        <CollageGrid photos={photos} gap="gap-px" />
      </div>
      <span className="text-[12px] font-bold text-white drop-shadow">{label}</span>
    </button>
  );
}

/** Swipeable reference photos with dots. One photo shows no dots. */
export function RefCarousel({
  urls,
  label,
  fallbackTitle,
  fallbackBody,
  corner,
}: {
  urls: string[];
  label: string;
  fallbackTitle: string;
  fallbackBody: string;
  /** Optional overlay in the bottom-right corner (After: "Your before"). */
  corner?: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [idx, setIdx] = useState(0);
  if (urls.length === 0) {
    return (
      <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1.5 rounded-[20px] border-2 border-dashed border-[#d3dee5] bg-white px-6 text-center">
        <div className="text-[19px] font-bold text-[#0f1720]">{fallbackTitle}</div>
        <div className="text-[15px] font-medium leading-snug text-[#64748b]">{fallbackBody}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-2.5">
      <div className="relative w-full overflow-hidden rounded-[20px] bg-[#e6edf1]">
        <div
          ref={ref}
          className="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          onScroll={(e) => {
            const el = e.currentTarget;
            setIdx(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
          }}
        >
          {urls.map((u) => (
            <img key={u} src={u} alt="" className="aspect-[4/3] w-full shrink-0 snap-center object-cover" />
          ))}
        </div>
        <div className="pointer-events-none absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1.5 text-[12.5px] font-bold text-[#0f1720]">
          {label}
        </div>
        {corner ? <div className="absolute bottom-3 right-3">{corner}</div> : null}
      </div>
      {urls.length > 1 ? (
        <div className="flex items-center gap-1.5">
          {urls.map((u, i) => (
            <span key={u} className={cn("h-1.5 rounded-full transition-all", i === idx ? "w-5 bg-[#15776d]" : "w-1.5 bg-[#c9d5dc]")} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Small labelled photo that opens full-screen when tapped. */
export function CornerThumb({
  url,
  label,
  onClick,
  className,
}: {
  url: string;
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button type="button" onClick={onClick} className={cn("flex flex-col items-end gap-1", className)}>
      <img src={url} alt="" className="h-[78px] w-[104px] rounded-[12px] border-2 border-white object-cover shadow-[0_4px_14px_rgba(15,23,32,0.25)]" />
      <span className="text-[12px] font-bold text-white drop-shadow">{label}</span>
    </button>
  );
}

export function PhotoOverlay({
  url,
  label,
  closeLabel,
  onClose,
}: {
  url: string;
  label: string;
  closeLabel: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-[#0d141b]">
      <img src={url} alt="" className="absolute inset-0 h-full w-full object-contain" />
      <div className="relative z-10 flex items-center justify-between px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))]">
        <span className="rounded-full bg-[rgba(13,20,27,0.7)] px-4 py-2 text-[14px] font-bold text-white">{label}</span>
        <button
          type="button"
          onClick={onClose}
          className="flex h-12 items-center gap-1.5 rounded-full bg-white px-5 text-[15.5px] font-bold text-[#0f1720]"
        >
          <X className="h-4 w-4" strokeWidth={2.6} />
          {closeLabel}
        </button>
      </div>
    </div>
  );
}

export function ProblemPill({ label, onClick, dark = false }: { label: string; onClick: () => void; dark?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-11 shrink-0 items-center gap-1.5 rounded-full px-4 text-[15px] font-bold",
        dark ? "bg-[rgba(13,20,27,0.62)] text-[#f6c978]" : "bg-[#fdf1ef] text-[#b4453a]",
      )}
    >
      <AlertTriangle className="h-4 w-4" strokeWidth={2.4} />
      {label}
    </button>
  );
}
